




































import {Component, Mixins, Prop, Ref, Vue, Watch} from 'vue-property-decorator'
import {ActiveInject, ActiveProvide} from "@/support/reactive"
import interact from 'interactjs'
import {uuidv4} from '@/design-system/utilities/Uuid';
import MonitorMixin from "@/design-system/utilities/MonitorMixin"
import {Location} from "vue-router"

/**
 * @status stable
 * @published 4.5.0
 * @changelog 4.5.0 Published.
 * @changelog 4.7.3 Remove excess console event.
 * @changelog 4.7.4 Clear document selection when selecting.
 * @changelog 4.7.5 Switch to blue during multiselect mode.
 * @changelog 4.8.1 Add drawer and badge support.
 * @changelog 4.8.5 Safeguard against error when clearing selection while no selection is active.
 * @changelog 4.9.2 Prevent user select on card as a whole.
 */
@Component({
    components: {}
})
export default class JdsPipelineCard extends Mixins(MonitorMixin) {
    @ActiveProvide('jds.pipeline.card.selected') selected = false
    @ActiveInject({from: 'jds.pipeline.stage.id', default: '' }) stageId!: string
    @ActiveInject({from: 'jds.pipeline.uid', default: null}) pipelineUid!: string|null
    @ActiveInject({from: 'jds.pipeline.bus', default: null}) bus!: Vue
    @ActiveInject({from: 'jds.pipeline.containerEl', default: null}) containerEl!: Vue
    @ActiveInject({from: 'jds.pipeline.is-selecting', default: false}) pipelineIsSelecting!: boolean
    @ActiveInject({from: 'jds.pipeline.select-mode', default: 'none'}) pipelineSelectMode!: string
    @ActiveInject({from: 'jds.pipeline.selected-stage', default: null}) pipelineSelectedStage!: string
    @ActiveInject({ from: 'jds.pipeline.dragging-cards', default: [] }) draggingCards!: string[]
    @ActiveInject({ from: 'jds.pipeline.selected-cards', default: [] }) selectedCards!: string[]

    @Ref() cardEl!: HTMLElement

    /**
     * Unique ID of this card. It only needs to be unique within the context of _this pipeline_ instance.
     *
     * Emitted as part of the pipeline's `drop` and `multiselect-change` events.
     */
    @Prop({ default: uuidv4 }) uid!: string
    @Prop() to!: Location
    width: number = 0
    height: number = 0

    disambiguationTimeout: number|null = null
    disambiguationEvent: PointerEvent|null = null     // holds the MouseEvent that triggered the click or drag
    disambiguationWait = 500            // after click, if no mouseup occurs in this window, we enter drag
    disambiguationThreshold = 2         // how many pixels of movement, from origin, we allow before drag starts

    mounted () {
        this.cardEl.addEventListener('pointerdown', this.handleDown)
        this.cardEl.addEventListener('pointermove', this.handleMove)
        this.cardEl.addEventListener('pointerup', this.handleUp)
        this.cardEl.addEventListener('pointercancel', this.handleUp)

        // if this card, on mount, is already selected, then it was probably dropped there while selected.
        // this means we should update which stage is currently the selection context.
        if (this.selectedCards.some(selectedCard => selectedCard === this.uid)) {
            this.$debug('Emitting cardSelectionMoved resync.')
            this.bus.$emit('cardSelectionMoved', {
                stageId: this.stageId
            })
        }
    }

    handleDown(e: PointerEvent) {
        this.$debug('PointerDown start')

        if (!this.cardEl) {
            // extremely rapid card movement can cause this to happen; ignore
            this.$info('PointerDown race condition, skipping.', 'CLICK_RACE')
            return
        }

        this.disambiguationEvent = e
        // register timeout to start drag
        this.disambiguationTimeout = window.setTimeout(this.startDrag, this.disambiguationWait)

        const selection = document.getSelection()
        if (selection) {
            selection.removeAllRanges();
        }
    }

    handleMove(e: PointerEvent) {
        if (e.pointerType === 'touch') {
            // touch mode: a move means the user meant to scroll. Cancel disambiguation, and ignore.
            this.resetDisambiguation()
            return
        } else {
            // mouse mode: if move is beyond threshold from start, begin drag
            if (this.disambiguationEvent) {
                if (Math.abs(this.disambiguationEvent.pageX - e.pageX) > this.disambiguationThreshold ||
                    Math.abs(this.disambiguationEvent.pageY - e.pageY) > this.disambiguationThreshold
                ) {
                    this.$debug('Mouse move beyond threshold, starting drag')
                    this.startDrag()
                }
            }
        }
    }

    handleUp(e: MouseEvent) {
        this.$debug('PointerUp fired')
        // if the card didn't pass thresholds, register click here
        if (this.disambiguationEvent) {
            this.$debug('Treating as a click')
            if (this.to) {
                this.$router.push(this.to)
            }
            this.$emit('click', e)
        }

        this.resetDisambiguation()
    }

    startDrag() {
        if (!this.disambiguationEvent) {
            this.$info('Tried to start drag, but no mouse event given.')
            return
        }

        // if there's a multiselection ongoing, don't allow drag on other cards
        if (this.selectedCards.length > 0 && !this.selected) {
            return
        }

        this.$debug('Emitting dragStart event on Pipeline bus.')
        this.bus.$emit('dragStart', {
            // these two represent the distance from the top left of the card to where the click started.
            offsetX: this.disambiguationEvent.clientX - this.cardEl.getBoundingClientRect().x,
            offsetY: this.disambiguationEvent.clientY - this.cardEl.getBoundingClientRect().y,

            // these two represent where the mouse is on the page, and the drag will be adjusted to match.
            clientX: this.disambiguationEvent.clientX,
            clientY: this.disambiguationEvent.clientY,
            width: this.cardEl.getBoundingClientRect().width,
            id: this.uid,
        })
        this.resetDisambiguation()
    }

    resetDisambiguation() {
        this.$debug('Clearing disambiguation queue.')
        if (this.disambiguationEvent) {
            if (this.disambiguationTimeout) {
                window.clearTimeout(this.disambiguationTimeout)
            }
            this.disambiguationTimeout = null
            this.disambiguationEvent = null
        }
    }

    handleKeyUp(e: KeyboardEvent) {
        if (e.key === 'Enter') {
            this.$debug('Treating as a click')
            if (this.to) {
                this.$router.push(this.to)
            }
            this.$emit('click', e)
        }
    }

    toggleSelection() {
        if (this.selected) {
            this.bus.$emit('cardDeselected', { cardId: this.uid })
        } else {
            this.bus.$emit('cardSelected', {
                cardId: this.uid,
                stageId: this.stageId,
            })
        }
        document.getSelection().removeAllRanges();
    }

    beforeDestroy() {
        this.resetDisambiguation()
    }

    @Watch('selectedCards', { immediate: true })
    updateSelectionState() {
        this.selected = this.selectedCards.some(selectedCard => selectedCard === this.uid);
    }

    @Watch('draggingCards', {deep: true})
    updateSize() {
        if (this.draggingCards.includes(this.uid)) {
            this.$debug('Setting size for drag phantom.')
            this.height = this.cardEl.getBoundingClientRect().height
            this.width = this.cardEl.getBoundingClientRect().width
        }
    }

    get allowSelect() {
        if (this.pipelineSelectMode === 'none') return false;
        if (this.pipelineSelectMode === 'per-stage' && this.pipelineIsSelecting) {
            if (this.selectedCards.length === 0) return true
            return this.stageId === this.pipelineSelectedStage
        }
        return false; // just a failsafe for invalid values
    }

    get phantomStyles() {
        const messiness = 70

        if (this.draggingCards.length === 1) {
            return {
                width: `${this.width}px`,
                height: `${this.height}px`,
            }
        }

        return {
            position: 'absolute',
            width: `${this.width}px`,
            height: `${this.height}px`,
            zIndex: this.draggingCards.indexOf(this.uid),
            // transform: `rotate(${(this.draggingCards.length - this.draggingCards.indexOf(this.id) - 1) * 5}deg)`
            transform: `rotate(${(messiness * 0.25) - (Math.random() * (messiness * 0.5))}deg) scale(${(1+(Math.random() * 0.2))})`,
            top: `${(messiness * 0.25) - (Math.random() * (messiness * 0.5))}px`,
            left: `${(messiness * 0.25) - (Math.random() * (messiness * 0.5))}px`,
        }
    }
}
